import type { ReaderGlossaryEntry, ReaderToken } from '@/types/reader'
import { DEFAULT_MAX_WORD_LENGTH } from './constants'

export type DictionaryCandidate = ReaderGlossaryEntry

export type DictionaryLookup = Map<string, DictionaryCandidate[]>

const HANZI_RE = /[\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff]/
const LATIN_OR_NUMBER_RE = /[A-Za-z0-9]/
const WHITESPACE_RE = /\s/
const PUNCTUATION_RE =
  /[\u3000-\u303f\uff00-\uffef，。！？、；：,.!?;:"'“”‘’（）()[\]{}《》〈〉「」『』—…·\-]/

function isHanzi(char: string) {
  return HANZI_RE.test(char)
}

function isLatinOrNumber(char: string) {
  return LATIN_OR_NUMBER_RE.test(char)
}

function isPunctuation(char: string) {
  return PUNCTUATION_RE.test(char)
}

function chooseCandidate(candidates: DictionaryCandidate[]) {
  return [...candidates].sort((a, b) => {
    const frequencyA = a.frequency ?? Number.MAX_SAFE_INTEGER
    const frequencyB = b.frequency ?? Number.MAX_SAFE_INTEGER
    if (frequencyA !== frequencyB) return frequencyA - frequencyB
    return a.id - b.id
  })[0]
}

export function createDictionaryLookup(entries: DictionaryCandidate[]) {
  const lookup: DictionaryLookup = new Map()

  for (const entry of entries) {
    const surfaces = new Set(
      [entry.simplified, entry.traditional].filter(Boolean) as string[]
    )

    for (const surface of surfaces) {
      const bucket = lookup.get(surface) ?? []
      bucket.push(entry)
      lookup.set(surface, bucket)
    }
  }

  return lookup
}

export function tokenizeText(
  text: string,
  lookup: DictionaryLookup = new Map(),
  options: { maxWordLength?: number } = {}
) {
  const maxWordLength = options.maxWordLength ?? DEFAULT_MAX_WORD_LENGTH
  const tokens: ReaderToken[] = []
  let index = 0

  while (index < text.length) {
    const char = text[index]

    if (!char) break

    if (WHITESPACE_RE.test(char)) {
      let end = index + 1
      while (end < text.length && WHITESPACE_RE.test(text[end])) end += 1
      tokens.push({ surface: text.slice(index, end), kind: 'punctuation' })
      index = end
      continue
    }

    if (isHanzi(char)) {
      let matchedSurface = ''
      let matchedEntry: DictionaryCandidate | undefined

      const remaining = Math.min(maxWordLength, text.length - index)
      for (let length = remaining; length > 0; length -= 1) {
        const surface = text.slice(index, index + length)
        if (![...surface].every(isHanzi)) continue

        const candidates = lookup.get(surface)
        if (candidates?.length) {
          matchedSurface = surface
          matchedEntry = chooseCandidate(candidates)
          break
        }
      }

      if (matchedEntry && matchedSurface) {
        tokens.push({
          surface: matchedSurface,
          simplified: matchedEntry.simplified,
          traditional: matchedEntry.traditional,
          pinyin: matchedEntry.pinyin,
          zhuyin: matchedEntry.zhuyin,
          entryId: matchedEntry.id,
          kind: 'word'
        })
        index += matchedSurface.length
      } else {
        tokens.push({ surface: char, kind: 'unknown' })
        index += 1
      }
      continue
    }

    if (isLatinOrNumber(char)) {
      let end = index + 1
      while (end < text.length && isLatinOrNumber(text[end])) end += 1
      tokens.push({ surface: text.slice(index, end), kind: 'latin' })
      index = end
      continue
    }

    tokens.push({
      surface: char,
      kind: isPunctuation(char) ? 'punctuation' : 'unknown'
    })
    index += 1
  }

  return tokens
}

export function extractChineseCandidates(
  texts: string[],
  options: { maxWordLength?: number } = {}
) {
  const maxWordLength = options.maxWordLength ?? DEFAULT_MAX_WORD_LENGTH
  const candidates = new Set<string>()

  for (const text of texts) {
    for (let start = 0; start < text.length; start += 1) {
      if (!isHanzi(text[start])) continue
      const maxLength = Math.min(maxWordLength, text.length - start)
      for (let length = 1; length <= maxLength; length += 1) {
        const surface = text.slice(start, start + length)
        if (![...surface].every(isHanzi)) break
        candidates.add(surface)
      }
    }
  }

  return [...candidates].sort(
    (a, b) => b.length - a.length || a.localeCompare(b)
  )
}
